import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import "./ImportActivity.css";

interface ActivitySummary {
  id?: string;
  type: string;
  start_time: number;
  duration_seconds: number;
  distance_meters?: number;
  avg_power?: number;
  avg_hr?: number;
  tss?: number;
}

export default function ImportActivity() {
  useAuth();
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activity, setActivity] = useState<ActivitySummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      await processFile(files[0]);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      await processFile(files[0]);
    }
  };

  const processFile = async (file: File) => {
    const validExtensions = [".fit", ".gpx", ".tcx"];
    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf("."));

    if (!validExtensions.includes(extension)) {
      setError("Invalid file type. Please upload a .fit, .gpx, or .tcx file.");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const data = await api.upload<ActivitySummary>(
        "/api/user/activities/import",
        formData,
      );

      setActivity(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
      setActivity(null);
    } finally {
      setUploading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="import-page">
      <div className="import-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>Import Activity</h1>
      </div>

      <div className="upload-section">
        <div
          className={`drop-zone ${dragActive ? "active" : ""} ${uploading ? "uploading" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {uploading ? (
            <div className="uploading-state">
              <div className="spinner" />
              <p>Processing activity...</p>
            </div>
          ) : (
            <>
              <div className="upload-icon">📁</div>
              <p className="upload-text">
                Drag and drop your activity file here
              </p>
              <p className="upload-hint">or</p>
              <label className="file-label">
                Browse Files
                <input
                  type="file"
                  accept=".fit,.gpx,.tcx"
                  onChange={handleFileSelect}
                  hidden
                />
              </label>
              <p className="file-types">.fit, .gpx, .tcx</p>
            </>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>

      {activity && (
        <div className="activity-preview">
          <h2>Activity Preview</h2>
          <div className="activity-card">
            <div className="activity-type">{activity.type}</div>
            <div className="activity-date">
              {formatDate(activity.start_time)}
            </div>
            <div className="activity-stats">
              <div className="stat">
                <span className="label">Duration</span>
                <span className="value">
                  {formatDuration(activity.duration_seconds)}
                </span>
              </div>
              {activity.distance_meters && (
                <div className="stat">
                  <span className="label">Distance</span>
                  <span className="value">
                    {(activity.distance_meters / 1000).toFixed(2)} km
                  </span>
                </div>
              )}
              {activity.avg_power && (
                <div className="stat">
                  <span className="label">Avg Power</span>
                  <span className="value">{activity.avg_power}W</span>
                </div>
              )}
              {activity.avg_hr && (
                <div className="stat">
                  <span className="label">Avg HR</span>
                  <span className="value">{activity.avg_hr} bpm</span>
                </div>
              )}
              {activity.tss && (
                <div className="stat highlight">
                  <span className="label">TSS</span>
                  <span className="value">{activity.tss}</span>
                </div>
              )}
            </div>
          </div>
          <div className="preview-actions">
            <button className="save-btn" onClick={() => navigate(-1)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
